import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import { apiGet, apiPost } from '../../services/api';
import { createRazorpayOrder, verifyRazorpayPayment } from '../../services/studentService';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';

// ── WhatsApp support number ─────────────────────────────────
const WA_SUPPORT = '919548354084';
const WA_MSG     = 'Hello PranaFlow Academy, my payment could not be completed. Please help me.';

/**
 * PranaFlow Academy – Enroll Confirm / Checkout Page
 *
 * Page States:
 *  'checkout'    → fee summary, coupon, pay button
 *  'processing'  → full-page overlay while Razorpay opens/pays/verifies
 *  'new_student' → password creation form (account created after verified payment)
 *  'existing'    → welcome back, login/dashboard redirect
 *  'enrolled'    → already enrolled in this batch
 *  'loggedin'    → logged-in student, redirecting to dashboard
 *  'activated'   → password set, redirecting to dashboard
 *  'failed'      → payment failed / verification failed, 10-s countdown
 */
const EnrollConfirm = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const { selectedCountry, formatPrice, convertPrice } = useCountry();
  const { user: authUser, setUser } = useAuth();
  const isLoggedIn = authUser && authUser.role === 'student';

  const { studentDetails, courseDetails, batchDetails } = location.state || {};

  // ── Page state machine ──────────────────────────────────────────────────────
  const [pageState, setPageState] = useState('checkout');

  // ── Coupon ──────────────────────────────────────────────────────────────────
  const [availablePublicCoupon, setAvailablePublicCoupon] = useState(null);
  const [couponCode, setCouponCode]                       = useState('');
  const [verifyingCoupon, setVerifyingCoupon]             = useState(false);
  const [couponStatus, setCouponStatus]                   = useState(null);
  const [couponMessage, setCouponMessage]                 = useState('');
  const [verifiedCoupon, setVerifiedCoupon]               = useState(null);

  // ── Full course record (for domestic / international charge %) ───────────────
  const [dbCourse, setDbCourse] = useState(null);
  useEffect(() => {
    if (!courseDetails?.id) return;
    (async () => {
      try {
        const courses = await apiGet('/public-courses.php');
        if (Array.isArray(courses)) {
          const match = courses.find(c => Number(c.id) === Number(courseDetails.id));
          if (match) setDbCourse(match);
        }
      } catch (e) {
        console.error('EnrollConfirm: failed to load course charge details', e);
      }
    })();
  }, [courseDetails?.id]);

  // ── Auto-apply public coupon if eligible ──────────────────────────────────────
  const autoCouponCheckedRef = useRef(false);
  useEffect(() => {
    if (!courseDetails?.id || autoCouponCheckedRef.current) return;
    autoCouponCheckedRef.current = true;

    const autoApplyPublicCoupon = async () => {
      try {
        const coupons = await apiGet('/public-coupons.php');
        if (Array.isArray(coupons) && coupons.length > 0) {
          // Find matching public coupon for this course_id first, then global (course_id = 0)
          let match = coupons.find(c => Number(c.course_id) === Number(courseDetails.id));
          if (!match) {
            match = coupons.find(c => Number(c.course_id) === 0);
          }
          if (match && match.coupon_code) {
            setAvailablePublicCoupon(match);
            const origFee = parseFloat(courseDetails.fee_inr) || 0;
            setVerifyingCoupon(true);
            const res = await apiPost('/verify-coupon.php', {
              course_id: Number(courseDetails.id),
              coupon_code: match.coupon_code.trim(),
              current_total_fee: origFee,
            });
            if (res && res.valid) {
              setCouponCode(match.coupon_code.trim().toUpperCase());
              setCouponStatus('success');
              setCouponMessage(res.message || 'Coupon applied!');
              setVerifiedCoupon(res);
            }
          }
        }
      } catch (err) {
        console.error("Auto-coupon detection error:", err);
      } finally {
        setVerifyingCoupon(false);
      }
    };

    autoApplyPublicCoupon();
  }, [courseDetails?.id]);

  // ── Payment type ────────────────────────────────────────────────────────────
  const [paymentType, setPaymentType] = useState('full_payment');

  // ── Processing stage text ───────────────────────────────────────────────────
  const [processingStage, setProcessingStage] = useState(0);
  const processingStages = [
    { icon: '🔐', text: 'Connecting to secure payment gateway...' },
    { icon: '💳', text: 'Processing your payment securely...' },
    { icon: '✅', text: 'Verifying payment signature...' },
    { icon: '📋', text: 'Finalizing your enrollment...' },
  ];
  const processingIntervalRef = useRef(null);

  // ── Post-payment data ───────────────────────────────────────────────────────
  const [verifyResult, setVerifyResult]     = useState(null);
  const [verifiedPaymentId, setVerifiedPaymentId] = useState('');
  const [failureReason, setFailureReason]   = useState('');
  const [notifWarning, setNotifWarning]     = useState('');

  // ── Password creation ───────────────────────────────────────────────────────
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submittingPass, setSubmittingPass]   = useState(false);
  const [passError, setPassError]             = useState('');
  const [showPass, setShowPass]               = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  // ── Failed-page countdown ───────────────────────────────────────────────────
  const [countdown, setCountdown] = useState(10);
  const countdownRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Confirm Enrollment & Payment – PranaFlow Academy';

    // Prevent search engines from indexing or following this transactional checkout page
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex, nofollow');

    return () => {
      clearInterval(processingIntervalRef.current);
      clearInterval(countdownRef.current);
      // Remove the robots meta on unmount so it does not bleed into other pages
      const existingRobotsMeta = document.querySelector('meta[name="robots"]');
      if (existingRobotsMeta) {
        existingRobotsMeta.remove();
      }
    };
  }, []);

  // Start countdown when failed page shown
  useEffect(() => {
    if (pageState !== 'failed') return;

    setCountdown(10);
    const interval = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [pageState]);

  // Watch countdown and handle redirect when it hits 0
  useEffect(() => {
    if (pageState === 'failed' && countdown <= 0) {
      navigate(location.pathname, { state: location.state, replace: true });
      setPageState('checkout');
    }
  }, [countdown, pageState, navigate, location.pathname, location.state]);

  // Auto-redirect for logged-in / existing students
  useEffect(() => {
    if (pageState === 'loggedin' || pageState === 'activated') {
      const t = setTimeout(() => navigate('/student/dashboard'), 3500);
      return () => clearTimeout(t);
    }
    if (pageState === 'existing') {
      const t = setTimeout(() => {
        if (isLoggedIn) navigate('/student/dashboard');
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [pageState]);

  // ── Guard ────────────────────────────────────────────────────────────────────
  if (!studentDetails || !courseDetails || !batchDetails) {
    return (
      <div className="public-site">
        <PublicHeader />
        <section style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)' }}>
          <div style={{ textAlign: 'center', maxWidth: 520, padding: '40px 24px' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: 'var(--text-primary)', marginBottom: 12 }}>Session Expired</h2>
            <p style={{ color: 'var(--text-body)', marginBottom: 24 }}>We couldn't find your enrollment details. Please go back to the course page and fill out the enrollment form again.</p>
            <button className="btn btn-primary" onClick={() => navigate('/courses')}>Go to Courses</button>
          </div>
        </section>
        <PublicFooter />
      </div>
    );
  }

  // ── Fee calculations ─────────────────────────────────────────────────────────
  // All INR base amounts (no charges yet) — charges are applied at display time
  // via formatPrice(n, dbCourse) which reads domestic_charges_pct / international_charges_pct
  const originalFee     = parseFloat(courseDetails.fee_inr) || 0;
  const finalFee        = verifiedCoupon ? parseFloat(verifiedCoupon.final_fee)        : originalFee;
  const discountAmount  = verifiedCoupon ? parseFloat(verifiedCoupon.discount_amount)  : 0;
  const canInstallment  = finalFee > 7000;  // threshold in INR

  useEffect(() => {
    if (!canInstallment && paymentType === 'installment') {
      setPaymentType('full_payment');
    }
  }, [canInstallment, paymentType]);

  // Installment plan is computed from INR amounts; each amount is displayed via
  // fmtAmt which applies charges, so totals remain mathematically consistent.
  const getInstallmentPlan = (totalFee, dur) => {
    const d = (dur || '').toLowerCase();
    let months = 1;
    const m = d.match(/(\d+)/);
    if (m) {
      const n = parseInt(m[1]);
      if (d.includes('month')) months = n;
      else if (d.includes('day')) months = Math.ceil(n / 30);
    }
    const twoInst = months <= 2;
    if (twoInst) {
      const a = parseFloat((totalFee * 0.5).toFixed(2));
      return [
        { label: '1st Installment (50%)', amount: a, when: 'Pay Now' },
        { label: '2nd Installment (50%)', amount: parseFloat((totalFee - a).toFixed(2)), when: 'After 15 Days' },
      ];
    }
    const a = parseFloat((totalFee * 0.35).toFixed(2));
    const b = parseFloat((totalFee * 0.35).toFixed(2));
    return [
      { label: '1st Installment (35%)', amount: a, when: 'Pay Now' },
      { label: '2nd Installment (35%)', amount: b, when: 'Next Month' },
      { label: '3rd Installment (30%)', amount: parseFloat((totalFee - a - b).toFixed(2)), when: 'Following Month' },
    ];
  };

  const installmentPlan = getInstallmentPlan(finalFee, courseDetails.duration);
  const amountNow       = paymentType === 'installment' ? installmentPlan[0].amount : finalFee;

  // ── Coupon verify helper ──────────────────────────────────────────────────────
  const applyCouponCode = async (codeToApply) => {
    if (!codeToApply || !codeToApply.trim()) return;
    const cleanCode = codeToApply.trim().toUpperCase();
    setCouponCode(cleanCode);
    setVerifyingCoupon(true);
    setCouponStatus(null);
    setCouponMessage('');
    try {
      const res = await apiPost('/verify-coupon.php', {
        course_id: Number(courseDetails.id),
        coupon_code: cleanCode,
        current_total_fee: originalFee,
      });
      if (res && res.valid) {
        setCouponStatus('success');
        setCouponMessage(res.message || 'Coupon applied!');
        setVerifiedCoupon(res);
      } else {
        setCouponStatus('error');
        setCouponMessage(res?.message || 'Invalid coupon code.');
        setVerifiedCoupon(null);
      }
    } catch (e) {
      setCouponStatus('error');
      setCouponMessage(e.message || 'Coupon verification failed.');
      setVerifiedCoupon(null);
    } finally {
      setVerifyingCoupon(false);
    }
  };

  const handleVerifyCoupon = () => {
    applyCouponCode(couponCode);
  };

  // ── Razorpay script ──────────────────────────────────────────────────────────
  const loadRazorpay = () => new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // ── Start processing stage cycling ──────────────────────────────────────────
  const startProcessingCycle = () => {
    setProcessingStage(0);
    let i = 0;
    processingIntervalRef.current = setInterval(() => {
      i = (i + 1) % processingStages.length;
      setProcessingStage(i);
    }, 2200);
  };
  const stopProcessingCycle = () => clearInterval(processingIntervalRef.current);

  // ── Main pay handler ─────────────────────────────────────────────────────────
  const handlePay = async (e) => {
    e.preventDefault();
    setFailureReason('');
    setPageState('processing');
    startProcessingCycle();

    try {
      // Stage 0 already shown — create order
      // IMPORTANT: UI sets paymentType to 'installment' (no 's').
      // The check must match exactly what the UI writes into state.
      const isInstallment = paymentType === 'installment' || paymentType === 'installments';
      const resolvedPaymentType = isInstallment ? 'installment' : 'full_payment';

      const payload = {
        payment_type:    resolvedPaymentType,
        course_id:       Number(courseDetails.id),
        batch_id:        Number(batchDetails.id),
        student_id:      isLoggedIn ? Number(authUser.id) : null,
        enrollment_id:   null,
        // Send the amount the user actually selected (full fee or 1st installment).
        // Backend validates independently from the DB — this is for reference only.
        amount:          amountNow,
        currency_code:   selectedCountry?.currency_code || 'INR',
        country_code:    selectedCountry?.country_code || 'IN',
        purpose:         isInstallment ? 'installment_payment' : 'full_course_payment',
        
        // Checkout fields
        original_fee:    originalFee,
        coupon_code:     verifiedCoupon ? (verifiedCoupon.coupon_code || couponCode.trim().toUpperCase()) : null,
        discount_amount: discountAmount,
        final_fee:       finalFee,
        studentDetails:  studentDetails,
        
        // Installment fields — installment_number 1 for first payment, null for full payment
        installment_id: null,
        installment_number: isInstallment ? 1 : null,
        demo_course_id: null,
        preferred_date: null,
        preferred_time: null
      };

      const orderRes = await createRazorpayOrder(payload);

      if (!orderRes || (!orderRes.razorpay_order_id && !orderRes.order_id)) {
        throw new Error('Server did not return a valid payment order. Please try again.');
      }

      const orderId = orderRes.order_id || orderRes.razorpay_order_id;

      const scriptOk = await loadRazorpay();
      if (!scriptOk) throw new Error('Failed to load Razorpay SDK. Please check your internet connection and try again.');

      setProcessingStage(1);

      // Open Razorpay modal
      await new Promise((resolve, reject) => {
        const options = {
          key:         orderRes.key_id,
          amount:      orderRes.amount_paise || (orderRes.amount * 100),
          currency:    orderRes.currency,
          name:        'PranaFlow Academy',
          description: `${courseDetails.title} – ${paymentType === 'installment' ? '1st Installment' : 'Full Payment'}`,
          order_id:    orderId,
          prefill: {
            name:    studentDetails.name  || '',
            email:   studentDetails.email || '',
            contact: studentDetails.phone || '',
          },
          theme:  { color: '#0d443e' },
          modal: {
            ondismiss: () => {
              stopProcessingCycle();
              setPageState('checkout');
              resolve();
            },
          },
          handler: async (response) => {
            setProcessingStage(2);
            try {
              const verifyRes = await verifyRazorpayPayment({
                payment_record_id:   orderRes.payment_record_id,
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              });

              setProcessingStage(3);
              await new Promise(r => setTimeout(r, 800)); // brief pause so stage 3 shows

              stopProcessingCycle();
              setVerifyResult(verifyRes);
              setVerifiedPaymentId(response.razorpay_payment_id);

              // Trigger Google Tag Manager purchase conversion event
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: 'pranaflow_purchase_success',
                value: orderRes.amount,
                currency: orderRes.currency,
                transaction_id: response.razorpay_payment_id
              });

              if (verifyRes.notification_warning) setNotifWarning(verifyRes.notification_warning);

              const status = verifyRes.student_status || (verifyRes.is_new_student ? 'new_student' : 'existing_student');

              if (verifyRes.already_enrolled) {
                setPageState('enrolled');
              } else if (isLoggedIn) {
                setPageState('loggedin');
              } else if (status === 'new_student') {
                setPageState('new_student');
              } else {
                setPageState('existing');
              }
              resolve();
            } catch (err) {
              stopProcessingCycle();
              setFailureReason(err.message || 'Payment verification failed. Please contact admin with your payment ID.');
              setPageState('failed');
              resolve();
            }
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      });

    } catch (err) {
      stopProcessingCycle();
      setFailureReason(err.message || 'Payment initiation failed. Please try again.');
      setPageState('failed');
    }
  };

  // ── Set password ─────────────────────────────────────────────────────────────
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPassError('');
    if (!password) { setPassError('Password is required.'); return; }
    if (password.length < 8) { setPassError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setPassError('Passwords do not match.'); return; }
    if (!verifyResult?.student_id) { setPassError('Student ID missing. Please contact admin.'); return; }

    setSubmittingPass(true);
    try {
      const res = await apiPost('/set-student-password.php', {
        student_id:          verifyResult.student_id,
        razorpay_payment_id: verifiedPaymentId,
        password,
      });
      if (res?.token && res?.user) {
        localStorage.setItem('token', res.token);
        setUser(res.user);
      }
      setPageState('activated');
    } catch (err) {
      setPassError(err.message || 'Failed to set password. Please contact admin.');
    } finally {
      setSubmittingPass(false);
    }
  };

  // ── Styles helpers ───────────────────────────────────────────────────────────
  const card = (extra = {}) => ({
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)',
    padding: '28px',
    ...extra,
  });

  const lbl = { color: 'var(--text-light)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 };
  const val = { color: 'var(--text-primary)', fontWeight: 700, fontSize: '0.95rem' };

  // fmtAmt — formats an INR base amount WITH domestic/international charges applied.
  // Use for all pre-payment checkout amounts (originalFee, finalFee, discountAmount,
  // installment portions). The charge % comes from dbCourse fetched above.
  const fmtAmt = (n) =>
    (!n || parseFloat(n) <= 0)
      ? 'Fee will be updated soon'
      : (formatPrice ? formatPrice(n, dbCourse) : `₹${(+n || 0).toLocaleString('en-IN')}`);

  // fmtPaid — formats an amount that is already in the gateway currency (post-payment
  // amounts from verifyResult). Do NOT apply charges again.
  const _currSymbol = selectedCountry?.currency_symbol || '₹';
  const _currCode   = selectedCountry?.currency_code   || 'INR';
  const fmtPaid = (n) => {
    if (!n || parseFloat(n) <= 0) return '—';
    const num = parseFloat(n);
    const formatted = num.toLocaleString(undefined, {
      minimumFractionDigits: num % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `${_currSymbol}${formatted} ${_currCode}`;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="public-site" style={{ background: 'var(--bg-base)' }}>
      {/* Inject keyframe animations */}
      <style>{`
        @keyframes pf-spin { to { transform: rotate(360deg); } }
        @keyframes pf-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.6; transform:scale(1.06); } }
        @keyframes pf-fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pf-dash { to { stroke-dashoffset: 0; } }
        @keyframes pf-pop { 0%{transform:scale(0.4);opacity:0} 70%{transform:scale(1.1);opacity:1} 100%{transform:scale(1);opacity:1} }
        @keyframes pf-countdown { from { stroke-dashoffset: 0; } to { stroke-dashoffset: 189; } }
        .pf-fadeUp { animation: pf-fadeUp 0.5s ease forwards; }
      `}</style>

      {/* ── PROCESSING OVERLAY ───────────────────────────────────────────────── */}
      {pageState === 'processing' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'linear-gradient(145deg, #0a2e2a 0%, #0d443e 60%, #163f39 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          {/* Outer ring */}
          <div style={{ position: 'relative', width: 140, height: 140, marginBottom: 40 }}>
            <svg width="140" height="140" style={{ position: 'absolute', top: 0, left: 0 }}>
              <circle cx="70" cy="70" r="62" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5"/>
              <circle cx="70" cy="70" r="62" fill="none" stroke="#a3c4a8" strokeWidth="5"
                strokeLinecap="round" strokeDasharray="390" strokeDashoffset="300"
                style={{ animation: 'pf-spin 1.8s linear infinite', transformOrigin: '70px 70px' }}/>
            </svg>
            {/* Inner logo block */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              width: 90, height: 90, borderRadius: '50%',
              background: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'pf-pulse 2s ease-in-out infinite',
            }}>
              <div style={{ textAlign: 'center', lineHeight: 1.1 }}>
                <div style={{ fontSize: '1.15rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.02em' }}>Prana</div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#a3c4a8', letterSpacing: '0.15em', textTransform: 'uppercase' }}>FLOW</div>
              </div>
            </div>
          </div>

          {/* Stage text */}
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{ fontSize: '1.8rem', marginBottom: 14 }}>
              {processingStages[processingStage].icon}
            </div>
            <h2 style={{ color: '#fff', fontSize: '1.3rem', fontWeight: 700, marginBottom: 12 }}>
              {processingStages[processingStage].text}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 28 }}>
              Please do not close or refresh this page.<br/>
              Your enrollment status will update automatically.
            </p>
            {/* Stage dots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              {processingStages.map((_, i) => (
                <div key={i} style={{
                  width: i === processingStage ? 24 : 8, height: 8,
                  borderRadius: 4, transition: 'all 0.4s ease',
                  background: i === processingStage ? '#a3c4a8' : 'rgba(255,255,255,0.2)',
                }}/>
              ))}
            </div>
          </div>

          {/* Secure badge */}
          <div style={{
            position: 'absolute', bottom: 32,
            display: 'flex', alignItems: 'center', gap: 8,
            color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem',
          }}>
            <span>🔒</span> Secured by Razorpay · 256-bit SSL Encryption
          </div>
        </div>
      )}

      {/* ── PAYMENT FAILED PAGE ──────────────────────────────────────────────── */}
      {pageState === 'failed' && (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', background: 'var(--bg-base)' }}>
          <div className="pf-fadeUp" style={{ ...card(), maxWidth: 580, width: '100%', textAlign: 'center' }}>

            {/* X icon */}
            <div style={{ width: 88, height: 88, borderRadius: '50%', background: '#fef2f2', border: '3px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', animation: 'pf-pop 0.5s ease forwards' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>

            <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: 'var(--text-primary)', marginBottom: 12 }}>Payment Failed</h1>

            {failureReason && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 20, fontSize: '0.875rem', color: '#dc2626', textAlign: 'left', fontWeight: 600 }}>
                ⚠ {failureReason}
              </div>
            )}

            <p style={{ color: 'var(--text-body)', fontSize: '1rem', lineHeight: 1.7, marginBottom: 28 }}>
              Your payment could not be completed. If any amount has been debited from your account, please wait for 24 hours or contact our support team with your payment proof.
            </p>

            {/* Countdown ring */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
              <div style={{ position: 'relative', width: 72, height: 72, marginBottom: 10 }}>
                <svg width="72" height="72" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="36" cy="36" r="30" fill="none" stroke="#f3f4f6" strokeWidth="5"/>
                  <circle cx="36" cy="36" r="30" fill="none" stroke="#ef4444" strokeWidth="5"
                    strokeLinecap="round" strokeDasharray="188.5"
                    strokeDashoffset={188.5 - (188.5 * countdown / 10)}
                    style={{ transition: 'stroke-dashoffset 0.9s linear' }}/>
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', fontWeight: 900, color: '#ef4444' }}>
                  {countdown}
                </div>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                Returning to checkout in {countdown} second{countdown !== 1 ? 's' : ''}…
              </p>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <a
                href={`https://wa.me/${WA_SUPPORT}?text=${encodeURIComponent(WA_MSG)}`}
                target="_blank" rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', borderRadius: 'var(--radius-full)',
                  background: '#25d366', color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                  textDecoration: 'none', border: 'none',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                Message Us on WhatsApp
              </a>

              <button
                className="btn btn-primary"
                onClick={() => {
                  clearInterval(countdownRef.current);
                  setPageState('checkout');
                  setFailureReason('');
                }}
                style={{ padding: '12px 28px', borderRadius: 'var(--radius-full)' }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW STUDENT — PASSWORD CREATION ─────────────────────────────────── */}
      {pageState === 'new_student' && (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #f0f7f1 0%, #fff 60%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px' }}>
          <div className="pf-fadeUp" style={{ maxWidth: 600, width: '100%' }}>

            {/* Welcome hero */}
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <div style={{ width: 88, height: 88, borderRadius: '50%', background: 'linear-gradient(135deg, #0d443e, #2d7a5a)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', animation: 'pf-pop 0.5s ease forwards' }}>
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2d7a5a', marginBottom: 8 }}>Payment Successful</div>
              <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.8rem,4vw,2.8rem)', color: '#0d443e', lineHeight: 1.2, marginBottom: 12 }}>
                Welcome to the<br/>PranaFlow Academy Family 🌿
              </h1>
              <p style={{ color: 'var(--text-body)', fontSize: '1.05rem', lineHeight: 1.6, maxWidth: 460, margin: '0 auto' }}>
                Your learning journey has officially begun. Create a password to activate your student account and access your dashboard.
              </p>

              {notifWarning && (
                <div style={{ marginTop: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: '0.82rem', color: '#92400e', fontWeight: 600 }}>
                  ℹ️ {notifWarning}
                </div>
              )}
            </div>

            {/* Enrollment summary card */}
            {verifyResult && (
              <div style={{ ...card({ marginBottom: 28 }) }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '16px 24px' }}>
                  <div><span style={lbl}>Course Enrolled</span><span style={val}>{verifyResult.course_name || courseDetails.title}</span></div>
                  <div><span style={lbl}>Batch / Cohort</span><span style={val}>{verifyResult.batch_name || batchDetails.name}</span></div>
                  <div>
                    <span style={lbl}>Amount Paid</span>
                    <span style={{ ...val, color: '#0d443e', fontSize: '1.1rem' }}>
                      {fmtAmt(verifyResult.amount_paid || amountNow)}
                    </span>
                  </div>
                  {(verifyResult.remaining_amount > 0) && (
                    <div>
                      <span style={lbl}>Remaining Balance</span>
                      <span style={{ ...val, color: '#ef4444' }}>{fmtAmt(verifyResult.remaining_amount)}</span>
                    </div>
                  )}
                  <div><span style={lbl}>Enrolled Email</span><span style={val}>{studentDetails.email}</span></div>
                  <div><span style={lbl}>Transaction ID</span><span style={{ ...val, fontSize: '0.8rem', wordBreak: 'break-all' }}>{verifiedPaymentId}</span></div>
                </div>
              </div>
            )}

            {/* Password form */}
            <div style={card()}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>Create Your Password</h3>
              <p style={{ color: 'var(--text-light)', fontSize: '0.88rem', marginBottom: 24 }}>
                Use this password to login to your student dashboard anytime.
              </p>

              <form onSubmit={handleSetPassword}>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="enroll-password" style={{ ...lbl, marginBottom: 6 }}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="enroll-password" type={showPass ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={password} onChange={e => setPassword(e.target.value)}
                      disabled={submittingPass} required
                      style={{ width: '100%', padding: '12px 42px 12px 16px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', fontSize: '1rem' }}>
                      {showPass ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="enroll-confirm-password" style={{ ...lbl, marginBottom: 6 }}>Confirm Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="enroll-confirm-password" type={showConfirmPass ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                      disabled={submittingPass} required
                      style={{ width: '100%', padding: '12px 42px 12px 16px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                    />
                    <button type="button" onClick={() => setShowConfirmPass(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', fontSize: '1rem' }}>
                      {showConfirmPass ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>

                {passError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: '0.875rem', fontWeight: 600 }}>
                    ⚠ {passError}
                  </div>
                )}

                <button
                  type="submit" id="activate-account-btn"
                  disabled={submittingPass}
                  style={{
                    width: '100%', padding: '14px', borderRadius: 'var(--radius-full)',
                    background: submittingPass ? '#999' : 'linear-gradient(135deg,#0d443e,#2d7a5a)',
                    color: '#fff', fontWeight: 800, fontSize: '1rem', border: 'none', cursor: submittingPass ? 'not-allowed' : 'pointer',
                    letterSpacing: '0.02em', transition: 'all 0.2s',
                  }}
                >
                  {submittingPass ? 'Activating Account...' : 'Activate My Account & Go to Dashboard →'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <span 
                    style={{ fontSize: '0.9rem', color: 'var(--text-light)', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                    onClick={() => navigate('/login')}
                  >
                    Skip for Now & Login Later
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── ACCOUNT ACTIVATED (after password set) ──────────────────────────── */}
      {pageState === 'activated' && (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'linear-gradient(160deg,#f0f7f1,#fff)' }}>
          <div className="pf-fadeUp" style={{ ...card({ textAlign: 'center', maxWidth: 520 }) }}>
            <div style={{ width: 88, height: 88, borderRadius: '50%', background: '#dcfce7', border: '3px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', animation: 'pf-pop 0.4s ease forwards' }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: '#0d443e', marginBottom: 12 }}>Account Activated! 🎉</h1>
            <p style={{ color: 'var(--text-body)', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: 24 }}>
              Welcome to PranaFlow Academy. Your account is ready. Taking you to your student dashboard now…
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: '#0d443e', animation: `pf-pulse ${1 + i * 0.2}s ease-in-out infinite` }}/>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── LOGGED-IN STUDENT (added new course) ────────────────────────────── */}
      {pageState === 'loggedin' && (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'linear-gradient(160deg,#f0f7f1,#fff)' }}>
          <div className="pf-fadeUp" style={{ ...card({ textAlign: 'center', maxWidth: 580 }) }}>
            <div style={{ width: 88, height: 88, borderRadius: '50%', background: '#dcfce7', border: '3px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', animation: 'pf-pop 0.4s ease forwards' }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', color: '#2d7a5a', marginBottom: 8, textTransform: 'uppercase' }}>Payment Successful</div>
            <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: '#0d443e', marginBottom: 12 }}>Course Added to Your Dashboard! 🌿</h1>
            {verifyResult && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '12px 20px', textAlign: 'left' }}>
                <div><span style={lbl}>Course</span><span style={val}>{verifyResult.course_name || courseDetails.title}</span></div>
                <div><span style={lbl}>Batch</span><span style={val}>{verifyResult.batch_name || batchDetails.name}</span></div>
                <div><span style={lbl}>Paid</span><span style={{ ...val, color: '#0d443e' }}>{fmtAmt(verifyResult.amount_paid || amountNow)}</span></div>
                {verifyResult.remaining_amount > 0 && <div><span style={lbl}>Remaining</span><span style={{ ...val, color: '#ef4444' }}>{fmtAmt(verifyResult.remaining_amount)}</span></div>}
              </div>
            )}
            <p style={{ color: 'var(--text-body)', fontSize: '0.95rem', marginBottom: 24 }}>Redirecting you to your student dashboard in a moment…</p>
            <button className="btn btn-primary" onClick={() => navigate('/student/dashboard')} style={{ borderRadius: 'var(--radius-full)', padding: '12px 32px' }}>
              Go to My Dashboard →
            </button>
          </div>
        </div>
      )}

      {/* ── EXISTING STUDENT (not logged in — new course added) ─────────────── */}
      {pageState === 'existing' && (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'linear-gradient(160deg,#f0f7f1,#fff)' }}>
          <div className="pf-fadeUp" style={{ ...card({ textAlign: 'center', maxWidth: 600 }) }}>
            <div style={{ width: 88, height: 88, borderRadius: '50%', background: 'linear-gradient(135deg,#0d443e,#2d7a5a)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', animation: 'pf-pop 0.4s ease forwards' }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', color: '#2d7a5a', marginBottom: 8, textTransform: 'uppercase' }}>Payment Successful</div>
            <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: '#0d443e', marginBottom: 12 }}>Welcome Back to PranaFlow Academy! 🌿</h1>
            <p style={{ color: 'var(--text-body)', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: 20, maxWidth: 440, margin: '0 auto 20px' }}>
              Your existing student account under <strong>{studentDetails.email}</strong> has been successfully linked to this enrollment. We are so happy to have you learn with us again!
            </p>
            {verifyResult && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '12px 20px', textAlign: 'left' }}>
                <div><span style={lbl}>New Course</span><span style={val}>{verifyResult.course_name || courseDetails.title}</span></div>
                <div><span style={lbl}>Batch</span><span style={val}>{verifyResult.batch_name || batchDetails.name}</span></div>
                <div><span style={lbl}>Paid</span><span style={{ ...val, color: '#0d443e' }}>{fmtAmt(verifyResult.amount_paid || amountNow)}</span></div>
                {verifyResult.remaining_amount > 0 && <div><span style={lbl}>Remaining</span><span style={{ ...val, color: '#ef4444' }}>{fmtAmt(verifyResult.remaining_amount)}</span></div>}
                <div><span style={lbl}>Account Email</span><span style={val}>{studentDetails.email}</span></div>
              </div>
            )}
            {notifWarning && <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: '0.82rem', color: '#92400e', fontWeight: 600, marginBottom: 20 }}>ℹ️ {notifWarning}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => navigate('/login')} style={{ borderRadius: 'var(--radius-full)', padding: '13px 32px', fontSize: '1rem' }}>
                Login to Your Student Dashboard →
              </button>
              <span 
                style={{ fontSize: '0.9rem', color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline', fontWeight: '700' }}
                onClick={() => navigate('/forgot-password')}
              >
                Forgot Password?
              </span>
            </div>
            <p style={{ color: 'var(--text-light)', fontSize: '0.82rem', marginTop: 12 }}>
              Login with your existing email and password to see your new course.
            </p>
          </div>
        </div>
      )}

      {/* ── ALREADY ENROLLED ────────────────────────────────────────────────── */}
      {pageState === 'enrolled' && (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg-base)' }}>
          <div className="pf-fadeUp" style={{ ...card({ textAlign: 'center', maxWidth: 520 }) }}>
            <div style={{ fontSize: '3.5rem', marginBottom: 20 }}>📋</div>
            <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: '#0d443e', marginBottom: 12 }}>Already Enrolled</h1>
            <p style={{ color: 'var(--text-body)', fontSize: '1rem', lineHeight: 1.7, marginBottom: 24 }}>
              You are already enrolled in <strong>{verifyResult?.course_name || courseDetails.title}</strong> – <strong>{verifyResult?.batch_name || batchDetails.name}</strong>.<br/>
              Your payment has been recorded. Please login to access your dashboard.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              {isLoggedIn
                ? <button className="btn btn-primary" onClick={() => navigate('/student/dashboard')} style={{ borderRadius: 'var(--radius-full)', padding: '12px 32px' }}>Go to My Dashboard →</button>
                : <button className="btn btn-primary" onClick={() => navigate('/login')} style={{ borderRadius: 'var(--radius-full)', padding: '12px 32px' }}>Login to Dashboard →</button>
              }
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── CHECKOUT PAGE (default state) ─────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── CHECKOUT PAGE (default state) ─────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {pageState === 'checkout' && (
        <>
          <PublicHeader />

          {/* Checkout-specific scoped CSS using PranaFlow Design System Tokens */}
          <style>{`
            /* ── Page Layout & Base ────────────────────────────── */
            .ec-page {
              min-height: 100vh;
              background: var(--bg-base, #FCFBF7);
              padding: 115px 0 64px;
              color: var(--text-primary, #1E2522);
              font-family: var(--body-font-family);
              box-sizing: border-box;
              overflow-x: hidden;
            }
            .ec-container {
              width: 100%;
              max-width: 1140px;
              margin: 0 auto;
              padding: 0 24px;
              box-sizing: border-box;
            }

            /* ── Top Heading & Progress Indicator ──────────────── */
            .ec-top-header {
              display: flex;
              align-items: flex-end;
              justify-content: space-between;
              gap: 20px;
              margin-bottom: 32px;
              flex-wrap: wrap;
            }
            .ec-heading-text {
              flex: 1;
              min-width: 260px;
            }
            .ec-heading-text h1.ec-main-title {
              font-family: var(--heading-font-family) !important;
              font-size: clamp(2.2rem, 4vw, 2.85rem);
              font-weight: 700;
              color: var(--primary-dark, #072B27) !important;
              margin: 0 0 6px;
              letter-spacing: -0.01em;
              line-height: 1.15;
            }
            .ec-heading-text p {
              font-family: var(--body-font-family);
              font-size: clamp(0.88rem, 2vw, 0.98rem);
              color: var(--text-body, #4A5350);
              margin: 0;
              font-weight: 500;
            }

            /* Progress Stepper */
            .ec-stepper {
              display: flex;
              align-items: center;
              gap: 0;
              max-width: 100%;
              flex-wrap: nowrap;
            }
            .ec-step-item {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 5px;
              flex-shrink: 0;
            }
            .ec-step-circle {
              width: clamp(34px, 4.5vw, 42px);
              height: clamp(34px, 4.5vw, 42px);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 1.5px solid var(--border, #E3E1D9);
              background: var(--bg-surface, #FFFFFF);
              transition: var(--transition, all 0.3s ease);
            }
            .ec-step-circle.done {
              border-color: var(--border, #E3E1D9);
              background: var(--bg-sage, #E7EFEF);
              color: var(--primary, #0D443E);
            }
            .ec-step-circle.active {
              background: linear-gradient(135deg, var(--primary, #0D443E), var(--primary-light, #165C54));
              border-color: var(--primary, #0D443E);
              color: #FFFFFF;
              box-shadow: 0 4px 14px rgba(13, 68, 62, 0.25);
            }
            .ec-step-circle.inactive {
              background: var(--bg-surface, #FFFFFF);
              border-color: var(--border-light, #EBEAE4);
              color: var(--text-light, #7A8582);
            }
            .ec-step-name {
              font-size: clamp(0.64rem, 1.8vw, 0.76rem);
              font-weight: 600;
              color: var(--text-light, #7A8582);
              white-space: nowrap;
            }
            .ec-step-name.active {
              color: var(--primary-dark, #072B27);
              font-weight: 800;
            }
            .ec-step-connector {
              flex: 1;
              min-width: 14px;
              max-width: 48px;
              height: 1.5px;
              background: var(--border, #E3E1D9);
              margin: 0 6px 16px;
            }
            .ec-main-grid {
              display: grid;
              grid-template-columns: 1.35fr 1fr;
              gap: 28px;
              align-items: start;
            }
            .ec-left-card {
              background: var(--bg-surface, #FFFFFF);
              border-radius: 20px;
              border: 1px solid var(--border, #E3E1D9);
              box-shadow: var(--shadow-sm, 0 2px 8px rgba(13, 68, 62, 0.04));
              padding: clamp(16px, 3vw, 24px);
              box-sizing: border-box;
            }
            .ec-section-strip {
              background: var(--bg-sage, #E7EFEF);
              border-radius: 14px;
              padding: 12px 18px;
              display: flex;
              align-items: center;
              gap: 14px;
              margin-bottom: 20px;
              border: 1px solid rgba(13, 68, 62, 0.06);
            }
            .ec-strip-icon-box {
              width: 38px;
              height: 38px;
              border-radius: 50%;
              background: linear-gradient(135deg, var(--primary, #0D443E), var(--primary-light, #165C54));
              display: flex;
              align-items: center;
              justify-content: center;
              color: #FFFFFF;
              flex-shrink: 0;
            }
            .ec-strip-title {
              font-size: clamp(0.95rem, 2vw, 1.05rem);
              font-weight: 800;
              color: var(--primary-dark, #072B27);
              margin: 0;
            }
            .ec-rows-list {
              display: flex;
              flex-direction: column;
              gap: 0;
              margin-bottom: 24px;
            }
            .ec-row-item {
              display: flex;
              align-items: flex-start;
              padding: 12px 6px;
              border-bottom: 1px solid var(--border-light, #EBEAE4);
              gap: 12px;
              font-size: 0.92rem;
            }
            .ec-row-item:last-child {
              border-bottom: none;
            }
            .ec-row-left-icon {
              width: 22px;
              height: 22px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: var(--primary, #0D443E);
              flex-shrink: 0;
              margin-top: 2px;
            }
            .ec-row-text {
              flex: 1;
              min-width: 0;
              line-height: 1.5;
              word-break: break-word;
              overflow-wrap: anywhere;
            }
            .ec-row-label {
              color: var(--text-primary, #1E2522);
              font-weight: 700;
              margin-right: 4px;
            }
            .ec-row-val {
              color: var(--text-body, #4A5350);
              font-weight: 500;
            }
            .ec-row-val.email-highlight {
              color: var(--primary, #0D443E);
              font-weight: 600;
            }
            /* Compact Coupon Area inside Right Payment Panel */
            .ec-right-coupon-box {
              padding: 16px 24px 0;
            }
            .ec-avail-coupon-line {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 10px;
              font-size: 0.78rem;
              box-sizing: border-box;
            }
            .ec-avail-label {
              color: var(--text-light, #7A8582);
              font-size: 0.78rem;
              font-weight: 500;
              white-space: nowrap;
            }
            .ec-avail-code {
              color: var(--primary, #0D443E);
              font-weight: 800;
              font-size: 0.85rem;
              letter-spacing: 0.04em;
              white-space: nowrap;
            }
            .ec-avail-btn {
              padding: 3px 10px;
              border-radius: 6px;
              border: 1px solid var(--primary, #0D443E);
              background: var(--primary, #0D443E);
              color: #FFFFFF;
              font-weight: 700;
              font-size: 0.75rem;
              cursor: pointer;
              transition: var(--transition, all 0.2s ease);
              white-space: nowrap;
              line-height: 1.3;
              margin-left: 2px;
            }
            .ec-avail-btn:hover:not(:disabled) {
              background: var(--primary-dark, #072B27);
              border-color: var(--primary-dark, #072B27);
            }
            .ec-avail-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
            .ec-right-coupon-row {
              display: flex;
              gap: 10px;
              align-items: center;
            }
            .ec-right-coupon-input {
              flex: 1;
              min-width: 0;
              padding: 11px 14px;
              border: 1.5px solid var(--border, #E3E1D9);
              border-radius: 12px;
              font-size: 0.9rem;
              color: var(--text-primary, #1E2522);
              font-weight: 600;
              outline: none;
              transition: var(--transition, all 0.3s ease);
              background: var(--bg-surface, #FFFFFF);
              box-sizing: border-box;
            }
            .ec-right-coupon-input:focus {
              border-color: var(--primary, #0D443E);
            }
            .ec-right-coupon-btn {
              padding: 11px 20px;
              border-radius: 12px;
              border: 1.5px solid var(--primary, #0D443E);
              background: var(--primary, #0D443E);
              color: #FFFFFF;
              font-weight: 800;
              font-size: 0.88rem;
              cursor: pointer;
              transition: var(--transition, all 0.3s ease);
              white-space: nowrap;
              flex-shrink: 0;
            }
            .ec-right-coupon-btn:hover:not(:disabled) {
              background: var(--primary-dark, #072B27);
              border-color: var(--primary-dark, #072B27);
            }
            .ec-right-coupon-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
            .ec-right-coupon-msg {
              margin-top: 8px;
              font-size: 0.83rem;
              font-weight: 700;
              display: flex;
              align-items: center;
              gap: 6px;
              line-height: 1.4;
              word-break: break-word;
            }
            .ec-right-coupon-msg.success {
              color: #16A34A;
            }
            .ec-right-coupon-msg.error {
              color: #DC2626;
            }
            .ec-right-card {
              background: var(--bg-surface, #FFFFFF);
              border-radius: 20px;
              border: 1px solid var(--border, #E3E1D9);
              box-shadow: var(--shadow-sm, 0 2px 8px rgba(13, 68, 62, 0.04));
              overflow: hidden;
              position: sticky;
              top: 100px;
              box-sizing: border-box;
            }
            .ec-fee-dark-header {
              background: linear-gradient(135deg, var(--primary-dark, #072B27) 0%, var(--primary, #0D443E) 100%);
              padding: 24px 24px 22px;
              color: #FFFFFF;
              position: relative;
            }
            .ec-fee-dark-header::after {
              content: '🪷';
              position: absolute;
              right: 16px;
              bottom: 10px;
              font-size: 3.5rem;
              opacity: 0.08;
              pointer-events: none;
            }
            .ec-fee-dark-title {
              font-size: 1.15rem;
              font-weight: 800;
              margin: 0 0 2px;
              color: #FFFFFF;
            }
            .ec-fee-dark-subtitle {
              font-size: 0.78rem;
              color: rgba(255, 255, 255, 0.7);
              margin: 0 0 20px;
              font-weight: 500;
            }
            .ec-fee-dark-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 0.9rem;
              color: rgba(255, 255, 255, 0.88);
              margin-bottom: 12px;
              gap: 12px;
              flex-wrap: wrap;
            }
            .ec-fee-dark-divider {
              border: none;
              border-top: 1px dotted rgba(255, 255, 255, 0.25);
              margin: 14px 0;
            }
            .ec-fee-dark-total-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 12px;
              flex-wrap: wrap;
            }
            .ec-fee-dark-total-label {
              font-size: 0.95rem;
              font-weight: 800;
              color: #FFFFFF;
            }
            .ec-fee-dark-total-val {
              font-size: clamp(1.35rem, 3vw, 1.6rem);
              font-weight: 900;
              color: #FFFFFF;
              letter-spacing: -0.02em;
              word-break: break-word;
            }
            .ec-plan-body {
              padding: 22px 24px 20px;
            }
            .ec-plan-heading {
              font-size: 0.98rem;
              font-weight: 800;
              color: var(--primary-dark, #072B27);
              margin: 0 0 16px;
            }
            .ec-plan-option-card {
              width: 100%;
              border: 1.5px solid var(--border, #E3E1D9);
              border-radius: 14px;
              padding: 16px 18px;
              margin-bottom: 12px;
              cursor: pointer;
              transition: var(--transition, all 0.3s ease);
              background: var(--bg-surface, #FFFFFF);
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              box-sizing: border-box;
            }
            .ec-plan-option-card.selected {
              border-color: var(--primary, #0D443E);
              background: var(--bg-sage, #E7EFEF);
            }
            .ec-plan-option-card.disabled {
              opacity: 0.45;
              cursor: not-allowed;
            }
            .ec-plan-opt-left {
              display: flex;
              align-items: flex-start;
              gap: 14px;
              flex: 1;
              min-width: 0;
            }
            .ec-plan-radio-circle {
              width: 22px;
              height: 22px;
              border-radius: 50%;
              border: 2px solid var(--border, #E3E1D9);
              display: flex;
              align-items: center;
              justify-content: center;
              margin-top: 2px;
              flex-shrink: 0;
              transition: var(--transition, all 0.3s ease);
              background: var(--bg-surface, #FFFFFF);
            }
            .ec-plan-option-card.selected .ec-plan-radio-circle {
              border-color: var(--primary, #0D443E);
              background: var(--primary, #0D443E);
            }
            .ec-plan-radio-circle-dot {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #FFFFFF;
            }
            .ec-plan-opt-title {
              font-size: 0.95rem;
              font-weight: 800;
              color: var(--text-primary, #1E2522);
              margin: 0 0 3px;
            }
            .ec-plan-opt-sub {
              font-size: 0.8rem;
              color: var(--text-body, #4A5350);
              line-height: 1.4;
              font-weight: 500;
              word-break: break-word;
            }
            .ec-plan-check-badge {
              width: 24px;
              height: 24px;
              border-radius: 50%;
              background: var(--primary, #0D443E);
              color: #FFFFFF;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .ec-plan-icon-badge {
              width: 24px;
              height: 24px;
              color: var(--primary, #0D443E);
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .ec-inst-breakdown {
              margin-top: 10px;
              padding: 12px 14px;
              background: var(--bg-sage, #E7EFEF);
              border-radius: 12px;
              border: 1px dashed var(--primary-light, #165C54);
            }
            .ec-inst-breakdown-row {
              display: flex;
              justify-content: space-between;
              font-size: 0.82rem;
              padding: 4px 0;
              color: var(--text-body, #4A5350);
              gap: 8px;
            }
            .ec-rzp-box {
              padding: 0 24px 20px;
            }
            .ec-rzp-title {
              font-size: 0.92rem;
              font-weight: 800;
              color: var(--text-primary, #1E2522);
              margin: 0 0 4px;
            }
            .ec-rzp-sub {
              font-size: 0.78rem;
              color: var(--text-light, #7A8582);
              margin: 0 0 14px;
            }
            .ec-payment-badges-row {
              display: flex;
              align-items: center;
              gap: 8px;
              flex-wrap: wrap;
              margin-bottom: 22px;
            }
            .ec-pay-badge-pill {
              height: 28px;
              padding: 0 10px;
              border-radius: 6px;
              border: 1px solid var(--border-light, #EBEAE4);
              background: var(--bg-surface, #FFFFFF);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 0.72rem;
              font-weight: 800;
              color: var(--text-primary, #1E2522);
              box-shadow: 0 1px 3px rgba(0,0,0,0.04);
            }
            .ec-pay-badge-pill.visa { color: #1A1F71; font-style: italic; }
            .ec-pay-badge-pill.mc { color: #EB001B; }
            .ec-pay-badge-pill.rupay { color: #008479; }
            .ec-pay-badge-pill.upi { color: #0D723B; }
            .ec-pay-badge-pill.paytm { color: #002E6E; }
            .ec-main-pay-btn {
              width: 100%;
              padding: 16px 20px;
              border-radius: 12px;
              border: none;
              cursor: pointer;
              font-size: clamp(0.92rem, 2.2vw, 1.02rem);
              font-weight: 800;
              color: #FFFFFF;
              background: linear-gradient(135deg, var(--primary, #0D443E) 0%, var(--primary-light, #165C54) 100%);
              box-shadow: 0 6px 20px rgba(13, 68, 62, 0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              transition: var(--transition, all 0.3s ease);
              box-sizing: border-box;
            }
            .ec-main-pay-btn:hover {
              background: linear-gradient(135deg, var(--primary-dark, #072B27) 0%, var(--primary, #0D443E) 100%);
              box-shadow: 0 8px 24px rgba(13, 68, 62, 0.4);
              transform: translateY(-1px);
            }
            .ec-100-secure {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 6px;
              margin-top: 14px;
              font-size: 0.85rem;
              font-weight: 700;
              color: #16A34A;
            }
            .ec-bottom-security-banner {
              margin-top: 32px;
              background: var(--bg-sage, #E7EFEF);
              border-radius: 18px;
              padding: 16px 20px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              border: 1px solid var(--border, #E3E1D9);
              box-sizing: border-box;
            }
            .ec-sec-banner-left {
              display: flex;
              align-items: center;
              gap: 12px;
              flex: 1;
              min-width: 0;
            }
            .ec-sec-shield-icon {
              width: clamp(34px, 4vw, 42px);
              height: clamp(34px, 4vw, 42px);
              border-radius: 50%;
              background: linear-gradient(135deg, var(--primary, #0D443E), var(--primary-light, #165C54));
              display: flex;
              align-items: center;
              justify-content: center;
              color: #FFFFFF;
              flex-shrink: 0;
            }
            .ec-sec-banner-text {
              flex: 1;
              min-width: 0;
            }
            .ec-sec-banner-text strong {
              display: block;
              font-size: clamp(0.82rem, 2.2vw, 0.92rem);
              color: var(--primary-dark, #072B27);
              font-weight: 800;
              line-height: 1.3;
            }
            .ec-sec-banner-text span {
              display: block;
              font-size: clamp(0.74rem, 2vw, 0.82rem);
              color: var(--text-body, #4A5350);
              line-height: 1.3;
            }
            .ec-sec-banner-right {
              display: flex;
              align-items: baseline;
              gap: 4px;
              flex-shrink: 0;
              white-space: nowrap;
            }
            .ec-sec-banner-right span {
              font-size: clamp(0.66rem, 1.8vw, 0.74rem);
              color: var(--text-light, #7A8582);
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.03em;
              white-space: nowrap;
            }
            .ec-sec-banner-right strong {
              font-size: clamp(0.88rem, 2.5vw, 1.08rem);
              font-weight: 900;
              color: #0284C7;
              white-space: nowrap;
            }
            .ec-trust-bar {
              margin-top: 36px;
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 20px;
              padding-top: 20px;
              border-top: 1px solid var(--border, #E3E1D9);
            }
            .ec-trust-item {
              display: flex;
              align-items: flex-start;
              gap: 12px;
            }
            .ec-trust-icon {
              width: 36px;
              height: 36px;
              border-radius: 50%;
              background: var(--bg-sage, #E7EFEF);
              display: flex;
              align-items: center;
              justify-content: center;
              color: var(--primary, #0D443E);
              flex-shrink: 0;
            }
            .ec-trust-text strong {
              display: block;
              font-size: 0.85rem;
              color: var(--text-primary, #1E2522);
              font-weight: 800;
              margin-bottom: 2px;
            }
            .ec-trust-text span {
              font-size: 0.78rem;
              color: var(--text-light, #7A8582);
            }
            @media (max-width: 960px) {
              .ec-main-grid {
                grid-template-columns: 1fr;
                gap: 24px;
              }
              .ec-right-card {
                position: static;
              }
            }
            @media (max-width: 768px) {
              .ec-trust-bar {
                grid-template-columns: repeat(2, 1fr);
                gap: 16px;
              }
            }
            @media (max-width: 480px) {
              .ec-page {
                padding: 95px 0 40px;
              }
              .ec-container {
                padding: 0 14px;
              }
              .ec-top-header {
                flex-direction: column;
                align-items: flex-start;
                gap: 18px;
              }
              .ec-stepper {
                width: 100%;
                justify-content: space-between;
              }
              .ec-step-connector {
                min-width: 8px;
                margin: 0 4px 14px;
              }
              .ec-coupon-form-row {
                flex-direction: column;
                align-items: stretch;
              }
              .ec-coupon-field,
              .ec-coupon-apply-btn,
              .ec-coupon-remove-btn {
                width: 100%;
                text-align: center;
              }
              .ec-trust-bar {
                grid-template-columns: 1fr;
              }
              .ec-rzp-box {
                padding: 0 16px 16px;
              }
              .ec-plan-body {
                padding: 18px 16px 16px;
              }
              .ec-fee-dark-header {
                padding: 20px 16px 18px;
              }
            }
          `}</style>

          <div className="ec-page">
            <div className="ec-container">
              <div className="ec-top-header">
                <div className="ec-heading-text">
                  <h1 className="ec-main-title" style={{ fontFamily: 'var(--heading-font-family)' }}>Confirm & Pay</h1>
                  <p>Review your enrollment details and complete your payment.</p>
                </div>
                <div className="ec-stepper">
                  <div className="ec-step-item">
                    <div className="ec-step-circle done">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="ec-step-name">Select Course</span>
                  </div>
                  <div className="ec-step-connector"/>
                  <div className="ec-step-item">
                    <div className="ec-step-circle active">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12H15M9 16H13M7 4V20M4 7H20V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span className="ec-step-name active">Confirm & Pay</span>
                  </div>
                  <div className="ec-step-connector"/>
                  <div className="ec-step-item">
                    <div className="ec-step-circle inactive">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                        <path d="M2 10H22" stroke="currentColor" strokeWidth="1.8"/>
                      </svg>
                    </div>
                    <span className="ec-step-name">Payment</span>
                  </div>
                </div>
              </div>
              <div className="ec-main-grid">
                <div className="ec-left-card">
                  <div className="ec-section-strip">
                    <div className="ec-strip-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M20 21V19C20 16.7909 16.4183 15 12 15C7.58172 15 4 16.7909 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3 className="ec-strip-title">Student Information</h3>
                  </div>
                  <div className="ec-rows-list">
                    <div className="ec-row-item">
                      <div className="ec-row-left-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8"/><path d="M4 20C4 16.79 7.58 15 12 15C16.42 15 20 16.79 20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </div>
                      <div className="ec-row-text">
                        <span className="ec-row-label">Full Name: </span>
                        <span className="ec-row-val">{studentDetails.name}</span>
                      </div>
                    </div>

                    {studentDetails.father_name && (
                      <div className="ec-row-item">
                        <div className="ec-row-left-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8"/><path d="M4 20C4 16.79 7.58 15 12 15C16.42 15 20 16.79 20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                        </div>
                        <div className="ec-row-text">
                          <span className="ec-row-label">Father's Name: </span>
                          <span className="ec-row-val">{studentDetails.father_name}</span>
                        </div>
                      </div>
                    )}

                    <div className="ec-row-item">
                      <div className="ec-row-left-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 7L12 13L21 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </div>
                      <div className="ec-row-text">
                        <span className="ec-row-label">Email: </span>
                        <span className="ec-row-val email-highlight">{studentDetails.email}</span>
                      </div>
                    </div>

                    <div className="ec-row-item">
                      <div className="ec-row-left-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6.62 10.79C8.06 13.62 10.38 15.93 13.21 17.38L15.41 15.18C15.68 14.91 16.07 14.82 16.42 14.94C17.55 15.32 18.77 15.53 20 15.53C20.55 15.53 21 15.98 21 16.53V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.24 8.71 6.45 9.09 7.59C9.2 7.94 9.12 8.33 8.84 8.61L6.62 10.79Z" stroke="currentColor" strokeWidth="1.8" fill="none"/></svg>
                      </div>
                      <div className="ec-row-text">
                        <span className="ec-row-label">Mobile: </span>
                        <span className="ec-row-val">{studentDetails.countryCode} {studentDetails.phone}</span>
                      </div>
                    </div>

                    {studentDetails.address && (
                      <div className="ec-row-item">
                        <div className="ec-row-left-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9C5 13.25 12 22 12 22C12 22 19 13.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" stroke="currentColor" strokeWidth="1.8" fill="none"/></svg>
                        </div>
                        <div className="ec-row-text">
                          <span className="ec-row-label">Address: </span>
                          <span className="ec-row-val">{studentDetails.address}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* — Section 2: Selected Course & Batch — */}
                  <div className="ec-section-strip">
                    <div className="ec-strip-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 3L1 9L12 15L23 9L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M5 11.2V16.5C5 16.5 8 19.5 12 19.5C16 19.5 19 16.5 19 16.5V11.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <h3 className="ec-strip-title">Selected Course & Batch</h3>
                  </div>

                  <div className="ec-rows-list">
                    <div className="ec-row-item">
                      <div className="ec-row-left-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 7H4C2.89 7 2 7.89 2 9V19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V9C22 7.89 21.11 7 20 7Z" stroke="currentColor" strokeWidth="1.8"/><path d="M16 21V5C16 4.45 15.55 4 15 4H9C8.45 4 8 4.45 8 5V21" stroke="currentColor" strokeWidth="1.8"/></svg>
                      </div>
                      <div className="ec-row-text">
                        <span className="ec-row-label">Course: </span>
                        <span className="ec-row-val">{courseDetails.title}</span>
                      </div>
                    </div>

                    <div className="ec-row-item">
                      <div className="ec-row-left-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 7V12L15 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </div>
                      <div className="ec-row-text">
                        <span className="ec-row-label">Duration: </span>
                        <span className="ec-row-val">{courseDetails.duration}</span>
                      </div>
                    </div>

                    <div className="ec-row-item">
                      <div className="ec-row-left-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.8"/></svg>
                      </div>
                      <div className="ec-row-text">
                        <span className="ec-row-label">Cohort / Batch: </span>
                        <span className="ec-row-val">{batchDetails.name}</span>
                      </div>
                    </div>

                    <div className="ec-row-item">
                      <div className="ec-row-left-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10H21" stroke="currentColor" strokeWidth="1.8"/><path d="M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </div>
                      <div className="ec-row-text">
                        <span className="ec-row-label">Timing & Start: </span>
                        <span className="ec-row-val">{batchDetails.timing} · Starts {batchDetails.start_date}</span>
                      </div>
                    </div>
                  </div>

                </div>{/* end left card */}

                {/* ══ RIGHT COLUMN: PAYMENT PANEL ═══════════════════════════ */}
                <div className="ec-right-card">

                  {/* 1. Fee Summary Dark Header */}
                  <div className="ec-fee-dark-header">
                    <h3 className="ec-fee-dark-title">Fee Summary</h3>
                    <p className="ec-fee-dark-subtitle">All amounts are in {selectedCountry?.currency_code || 'INR'}</p>

                    <div className="ec-fee-dark-row">
                      <span>Original Tuition Fee</span>
                      <strong>{fmtAmt(originalFee)}</strong>
                    </div>

                    {discountAmount > 0 && (
                      <div className="ec-fee-dark-row" style={{ color: '#86EFAC' }}>
                        <span>Coupon Discount ({verifiedCoupon?.coupon_code})</span>
                        <strong>– {fmtAmt(discountAmount)}</strong>
                      </div>
                    )}

                    <hr className="ec-fee-dark-divider"/>

                    <div className="ec-fee-dark-total-row">
                      <span className="ec-fee-dark-total-label">Total Payable Fee</span>
                      <span className="ec-fee-dark-total-val">{fmtAmt(finalFee)}</span>
                    </div>
                  </div>

                  {/* 2. Compact Coupon Code Area (Above Choose Payment Plan) */}
                  <div className="ec-right-coupon-box">

                    {/* Single-Line Compact Available Coupon Shortcut */}
                    {availablePublicCoupon && (
                      <div className="ec-avail-coupon-line">
                        <span className="ec-avail-label">Available Coupon</span>
                        <strong className="ec-avail-code">{availablePublicCoupon.coupon_code}</strong>
                        <button
                          type="button"
                          className="ec-avail-btn"
                          onClick={() => applyCouponCode(availablePublicCoupon.coupon_code)}
                          disabled={verifyingCoupon}
                        >
                          {verifyingCoupon ? 'Verifying…' : 'Apply'}
                        </button>
                      </div>
                    )}

                    {/* Main Editable Coupon Input Row */}
                    <div className="ec-right-coupon-row">
                      <input
                        className="ec-right-coupon-input"
                        type="text"
                        placeholder="Coupon Code"
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        disabled={verifyingCoupon}
                      />
                      <button
                        type="button"
                        className="ec-right-coupon-btn"
                        onClick={handleVerifyCoupon}
                        disabled={verifyingCoupon || !couponCode.trim()}
                      >
                        {verifyingCoupon ? 'Verifying…' : 'Apply'}
                      </button>
                    </div>

                    {/* Status Feedback Message (Below Main Coupon Input) */}
                    {couponStatus === 'success' && verifiedCoupon && (
                      <div className="ec-right-coupon-msg success">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Coupon <strong>{verifiedCoupon.coupon_code}</strong> applied successfully.</span>
                      </div>
                    )}

                    {couponStatus === 'error' && (
                      <div className="ec-right-coupon-msg error">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="#DC2626" strokeWidth="2"/>
                          <path d="M12 8V12M12 16H12.01" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span>{couponMessage || 'Invalid or unavailable coupon code.'}</span>
                      </div>
                    )}
                  </div>

                  {/* 3. Choose Payment Plan Body */}
                  <div className="ec-plan-body">
                    <h4 className="ec-plan-heading">Choose Payment Plan</h4>

                    {/* Option 1: Pay Full Fee */}
                    <div
                      className={`ec-plan-option-card${paymentType === 'full_payment' ? ' selected' : ''}`}
                      onClick={() => setPaymentType('full_payment')}
                      role="radio"
                      aria-checked={paymentType === 'full_payment'}
                    >
                      <div className="ec-plan-opt-left">
                        <div className="ec-plan-radio-circle">
                          {paymentType === 'full_payment' && <div className="ec-plan-radio-circle-dot"/>}
                        </div>
                        <div>
                          <p className="ec-plan-opt-title">Pay Full Fee</p>
                          <p className="ec-plan-opt-sub">Pay {fmtAmt(finalFee)} now — done!</p>
                        </div>
                      </div>
                      {paymentType === 'full_payment' && (
                        <div className="ec-plan-check-badge">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Option 2: Pay in Installments */}
                    <div
                      className={`ec-plan-option-card${paymentType === 'installment' ? ' selected' : ''}${!canInstallment ? ' disabled' : ''}`}
                      onClick={() => { if (canInstallment) setPaymentType('installment'); }}
                      role="radio"
                      aria-checked={paymentType === 'installment'}
                    >
                      <div className="ec-plan-opt-left">
                        <div className="ec-plan-radio-circle">
                          {paymentType === 'installment' && <div className="ec-plan-radio-circle-dot"/>}
                        </div>
                        <div>
                          <p className="ec-plan-opt-title">Pay in Installments</p>
                          <p className="ec-plan-opt-sub">
                            Start with {fmtAmt(installmentPlan[0]?.amount || 0)}<br/>
                             Min fee &gt; ₹7,000
                          </p>
                        </div>
                      </div>
                      <div className="ec-plan-icon-badge">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                          <path d="M3 10H21" stroke="currentColor" strokeWidth="1.8"/>
                          <path d="M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </div>
                    </div>

                    {/* Installment breakdown if selected */}
                    {paymentType === 'installment' && canInstallment && (
                      <div className="ec-inst-breakdown">
                        <div style={{ fontWeight: 800, fontSize: '0.8rem', color: 'var(--primary, #0D443E)', marginBottom: 6 }}>
                          Installment Schedule
                        </div>
                        {installmentPlan.map((inst, i) => (
                          <div key={i} className="ec-inst-breakdown-row">
                            <span>{inst.label} ({inst.when})</span>
                            <strong>{fmtAmt(inst.amount)}</strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Razorpay Secure Checkout Section */}
                  <div className="ec-rzp-box">
                    <p className="ec-rzp-title">Razorpay Secure Checkout</p>
                    <p className="ec-rzp-sub">Pay with Cards, UPI, Netbanking, or Wallets · 256-bit SSL Encrypted</p>

                    <div className="ec-payment-badges-row">
                      <div className="ec-pay-badge-pill visa">VISA</div>
                      <div className="ec-pay-badge-pill mc">Mastercard</div>
                      <div className="ec-pay-badge-pill rupay">RuPay</div>
                      <div className="ec-pay-badge-pill upi">UPI</div>
                      <div className="ec-pay-badge-pill paytm">Paytm</div>
                    </div>

                    {/* CTA Button */}
                    <button
                      type="button"
                      id="proceed-payment-btn"
                      className="ec-main-pay-btn"
                      onClick={handlePay}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <path d="M8 11V7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Pay {fmtAmt(amountNow)} Securely →
                    </button>

                    <div className="ec-100-secure">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="#16A34A" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M9 12L11 14L15 10" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      100% Secure
                    </div>
                  </div>

                </div>{/* end right card */}
              </div>{/* end main grid */}

              {/* ── Bottom Payment Security Banner ── */}
              <div className="ec-bottom-security-banner">
                <div className="ec-sec-banner-left">
                  <div className="ec-sec-shield-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="ec-sec-banner-text">
                    <strong>Your payment is secured by Razorpay.</strong>
                    <span>We do not store card details.</span>
                  </div>
                </div>
                <div className="ec-sec-banner-right">
                  <span>Powered by</span>
                  <strong>Razorpay</strong>
                </div>
              </div>

              {/* ── Bottom 4-Column Trust Bar ── */}
              <div className="ec-trust-bar">
                <div className="ec-trust-item">
                  <div className="ec-trust-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="ec-trust-text">
                    <strong>Secure Payments</strong>
                    <span>256-bit SSL encryption</span>
                  </div>
                </div>

                <div className="ec-trust-item">
                  <div className="ec-trust-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M3 18V12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M21 19C21 20.1 20.1 21 19 21H18C16.9 21 16 20.1 16 19V16C16 14.9 16.9 14 18 14H21V19Z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 19C3 20.1 3.9 21 5 21H6C7.1 21 8 20.1 8 19V16C8 14.9 7.1 14 6 14H3V19Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div className="ec-trust-text">
                    <strong>Need Help?</strong>
                    <a href="tel:09548354084" style={{ fontSize: '0.78rem', color: 'var(--text-light, #7A8582)', textDecoration: 'none' }}>09548354084</a>
                  </div>
                </div>

                <div className="ec-trust-item">
                  <div className="ec-trust-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12C21 16.97 16.97 21 12 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M3 8V12H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="ec-trust-text">
                    <strong>Easy Refunds</strong>
                    <span>Hassle-free process</span>
                  </div>
                </div>

                <div className="ec-trust-item">
                  <div className="ec-trust-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                      <path d="M15.48 14.37L17 21L12 18L7 21L8.52 14.37" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="ec-trust-text">
                    <strong>Trusted by 500+</strong>
                    <span>Yoga aspirants</span>
                  </div>
                </div>
              </div>

            </div>{/* end ec-container */}
          </div>{/* end ec-page */}

          <PublicFooter />
        </>
      )}
    </div>
  );
};

export default EnrollConfirm;
